import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import {
  LoginRequest,
  RegisterRequest,
  TwoFAValidateRequest,
  TwoFASetupResponse,
  AuthResponse,
  User,
  Client,
  CreateClientRequest,
  UpdateClientRequest,
  ClientStats,
  QuotaInfo,
  StatsSummary,
  RequestStat,
  UpdateProfileRequest,
  Plan,
  Invoice,
  PaymentInfo,
  AllowedIP,
  IPRestrictionInfo
} from '../types';

// Créer une instance axios avec la configuration de base
const api = axios.create({
  // En production, utiliser des chemins relatifs si le frontend est servi par le backend
  // En développement, utiliser l'URL complète
  baseURL: process.env.NODE_ENV === 'production' 
    ? '/api' // Chemin relatif avec préfixe /api quand servi par le même serveur
    : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api'),
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token JWT aux requêtes
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Variable pour stocker la fonction de déconnexion du contexte
let logoutFunction: (() => void) | null = null;

// Fonction pour définir la fonction de déconnexion
export const setLogoutFunction = (fn: () => void) => {
  logoutFunction = fn;
};

// Intercepteur pour gérer les erreurs globalement
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Gérer les erreurs 401 (non autorisé) - session expirée
    if (error.response?.status === 401) {
      // Ne pas rediriger si l'erreur 401 concerne la demande de code 2FA
      const responseData = error.response?.data as any;
      const requires2FA = responseData?.requires2FA;
      
      if (!requires2FA) {
        // Uniquement pour les autres erreurs 401 (session expirée, etc.)
        // Utiliser la fonction de déconnexion du contexte si disponible
        if (logoutFunction) {
          logoutFunction();
        } else {
          // Fallback si le contexte n'est pas disponible
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Services d'authentification
export const authService = {
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/register', data);
    return response.data;
  },
  
  login: async (data: LoginRequest): Promise<AuthResponse> => {
    try {
      // Transformer otpCode en twoFACode pour le backend
      const requestData = {
        email: data.email,
        password: data.password,
        twoFACode: data.otpCode
      };
      
      const response = await api.post<AuthResponse>('/auth/login', requestData);
      return response.data;
    } catch (error: any) {
      // Si le backend indique que 2FA est requis
      if (error.response?.status === 401 && error.response?.data?.requires2FA) {
        console.log('2FA required, redirecting to verification page');
        // Retourner un objet conforme au type AuthResponse
        return {
          requires2FA: true,
          token: '', // Valeur vide car pas encore authentifié
          user: {} as User // Cast pour satisfaire TypeScript
        };
      }
      throw error;
    }
  },
  
  getCurrentUser: async (): Promise<User> => {
    const response = await api.get<{ user: User }>('/auth/me');
    return response.data.user;
  },
  
  setup2FA: async (): Promise<TwoFASetupResponse> => {
    const response = await api.get<{ qrCodeUrl: string, secret: string, message: string }>('/auth/2fa/setup');
    return {
      qrCodeUrl: response.data.qrCodeUrl,
      secret: response.data.secret
    };
  },
  
  validate2FA: async (data: TwoFAValidateRequest): Promise<{ success: boolean, message?: string, error?: string, status?: number }> => {
    try {
      // Transformer les données pour correspondre à ce qu'attend le backend
      const requestData = {
        token: data.code, // Le backend attend 'token' au lieu de 'code'
        secret: data.secret // Envoyer le secret pour les backends qui peuvent en avoir besoin
      };
      
      console.log('Envoi de la requête de validation 2FA avec les données:', { 
        token: data.code,
        secretLength: data.secret?.length || 0
      });
      
      // Appeler l'endpoint /auth/2fa/verify
      const response = await api.post<{ success: boolean, message: string }>('/auth/2fa/verify', requestData);
      console.log('Réponse de validation 2FA reçue:', response.data);
      return { success: true, message: response.data.message };
    } catch (error: any) {
      // Capturer le code de statut HTTP
      const status = error.response?.status;
      const message = error.response?.data?.error || 'Erreur lors de la validation du code';
      console.error(`Erreur de validation 2FA (${status}):`, message, error);  
      return { success: false, error: message, status };
    }
  },
  
  disable2FA: async (): Promise<{ success: boolean, message?: string, error?: string }> => {
    try {
      // Appeler l'endpoint pour désactiver la 2FA
      const response = await api.post<{ success: boolean, message: string }>('/auth/2fa/disable');
      return { success: true, message: response.data.message };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erreur lors de la désactivation de la 2FA';
      console.error('Erreur de désactivation 2FA:', message);
      return { success: false, error: message };
    }
  },
  
  updateProfile: async (data: UpdateProfileRequest): Promise<User> => {
    try {
      // Log des données envoyées (sans les mots de passe sensibles)
      console.log('Mise à jour du profil avec les données:', { 
        ...data, 
        currentPassword: data.currentPassword ? '[HIDDEN]' : undefined,
        newPassword: data.newPassword ? '[HIDDEN]' : undefined
      });
      
      // Retirer les champs de mot de passe de la requête de mise à jour du profil
      const { currentPassword, newPassword, confirmPassword, ...profileData } = data;
      
      const response = await api.put<{ user: User }>('/profile', profileData);
      console.log('Profil mis à jour avec succès');
      return response.data.user;
    } catch (error: any) {
      console.error('Erreur lors de la mise à jour du profil:', error.response?.data || error.message);
      throw error;
    }
  },
  
  changePassword: async (data: { currentPassword: string, newPassword: string }): Promise<{ success: boolean, message?: string, error?: string }> => {
    try {
      console.log('Changement de mot de passe...');
      const response = await api.put<{ message: string }>('/profile/password', data);
      return { success: true, message: response.data.message || 'Mot de passe changé avec succès' };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erreur lors du changement de mot de passe';
      console.error('Erreur de changement de mot de passe:', message);
      return { success: false, error: message };
    }
  },
  
  // Supprimer le compte utilisateur (RGPD)
  deleteAccount: async (password: string): Promise<{ success: boolean, message?: string, error?: string }> => {
    try {
      const response = await api.delete<{ message: string }>('/profile', { data: { password } });
      return { success: true, message: response.data.message || 'Compte supprimé avec succès' };
    } catch (error: any) {
      const message = error.response?.data?.error || 'Erreur lors de la suppression du compte';
      console.error('Erreur de suppression de compte:', message);
      return { success: false, error: message };
    }
  }
};

// Service de statistiques
export const statsService = {
  // Récupérer les statistiques globales
  getGlobalStats: async (): Promise<StatsSummary> => {
    const response = await api.get<{ stats: StatsSummary }>('/stats/global');
    return response.data.stats;
  },

  // Récupérer les statistiques d'un client
  getClientStats: async (clientId: string): Promise<ClientStats> => {
    const response = await api.get<{ stats: ClientStats }>(`/stats/client/${clientId}`);
    return response.data.stats;
  },

  // Récupérer les quotas d'un client
  getClientQuota: async (clientId: string): Promise<QuotaInfo> => {
    const response = await api.get<{ quota: QuotaInfo }>(`/stats/client/${clientId}/quota`);
    return response.data.quota;
  }
};

// Service d'abonnement et de facturation
export const subscriptionService = {
  // Récupérer tous les plans disponibles
  getPlans: async (): Promise<Plan[]> => {
    const response = await api.get<{ plans: Plan[] }>('/subscription/plans');
    return response.data.plans || [];
  },

  // Récupérer un plan spécifique par son ID
  getPlanById: async (planId: string): Promise<Plan> => {
    const response = await api.get<{ plan: Plan }>(`/subscription/plans/${planId}`);
    return response.data.plan;
  },

  // Récupérer l'historique des factures
  getInvoices: async (): Promise<Invoice[]> => {
    const response = await api.get<{ invoices: Invoice[] }>('/subscription/invoices');
    return response.data.invoices || [];
  },

  // Récupérer les informations de paiement
  getPaymentInfo: async (): Promise<PaymentInfo | null> => {
    const response = await api.get<PaymentInfo | null>('/subscription/payment-info');
    return response.data;
  },

  // Changer de plan d'abonnement
  changePlan: async (planId: string, currency?: string): Promise<User | { sessionId?: string, sessionUrl?: string, message: string, success?: boolean }> => {
    const response = await api.post('/subscription/change-plan', { planId, currency });
    return response.data;
  },

  // Annuler l'abonnement actuel
  cancelSubscription: async (): Promise<{ success: boolean, message: string, user: User }> => {
    const response = await api.post('/subscription/cancel');
    return response.data;
  },

  // Vérifier la session Stripe après un paiement réussi
  verifySession: async (sessionId: string): Promise<User> => {
    const response = await api.get<User>(`/subscription/verify-session/${sessionId}`);
    return response.data;
  },

  // Mettre à jour les informations de paiement
  updatePaymentInfo: async (data: PaymentInfo): Promise<{ message: string }> => {
    const response = await api.put('/subscription/payment-info', data);
    return response.data;
  }
};

// Services de gestion des clients
export const clientService = {
  getAllClients: async (): Promise<Client[]> => {
    const response = await api.get<{ clients: Client[] }>('/clients');
    return response.data.clients;
  },
  
  getClientById: async (id: string): Promise<Client> => {
    const response = await api.get<{ client: Client }>(`/clients/${id}`);
    return response.data.client;
  },
  
  createClient: async (data: CreateClientRequest): Promise<Client> => {
    const response = await api.post<{ message: string, client: Client }>('/clients', data);
    return response.data.client;
  },
  
  updateClient: async (id: string, data: UpdateClientRequest): Promise<Client> => {
    const response = await api.put<{ message: string, client: Client }>(`/clients/${id}`, data);
    return response.data.client;
  },
  
  deleteClient: async (id: string): Promise<void> => {
    await api.delete(`/clients/${id}`);
  },
  
  regenerateApiKey: async (id: string): Promise<{ apiKey: string }> => {
    const response = await api.post<{ message: string, apiKey: string }>(`/clients/${id}/apikey`);
    return response.data;
  },
  
  getAgentDownloadUrl: (id: string): string => {
    return `${api.defaults.baseURL}/clients/${id}/download`;
  },
  
  downloadAgent: async (clientId: string): Promise<Blob> => {
    const response = await api.get(`/clients/${clientId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },
  
  getClientStats: async (clientId: string, period: 'day' | 'week' | 'month' | 'year' = 'day', startDate?: Date, endDate?: Date): Promise<{ stats: RequestStat[], summary: StatsSummary, quota: QuotaInfo }> => {
    let url = `/stats/clients/${clientId}?period=${period}`;
    
    if (startDate && endDate) {
      url += `&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
    }
    
    const response = await api.get(url);
    
    // Convertir les dates string en objets Date
    const stats = response.data.stats.map((stat: any) => ({
      ...stat,
      date: new Date(stat.date)
    }));
    
    const summary = {
      ...response.data.summary,
      period: {
        start: new Date(response.data.summary.period.start),
        end: new Date(response.data.summary.period.end)
      }
    };
    
    return {
      stats,
      summary,
      quota: response.data.quota
    };
  },
  
  getStatsOverview: async (): Promise<{ clientStats: ClientStats[], quota: QuotaInfo }> => {
    const response = await api.get('/stats/summary');
    
    return response.data;
  },
  
  getClientStatus: async (id: string): Promise<{ status: 'online' | 'offline', lastSeen?: Date, stats?: any }> => {
    try {
      // Créer une instance axios séparée qui n'a pas l'intercepteur de redirection
      const statusApi = axios.create({
        baseURL: api.defaults.baseURL,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const response = await statusApi.get<{ status: 'online' | 'offline', lastSeen: string, stats: any }>(
        `/clients/${id}/status`
      );
      
      return {
        status: response.data.status,
        lastSeen: response.data.lastSeen ? new Date(response.data.lastSeen) : undefined,
        stats: response.data.stats
      };
    } catch (error) {
      // En cas d'erreur 401 ou autre, on retourne simplement un statut offline
      // sans déclencher la redirection automatique
      console.log('Erreur lors de la récupération du statut du client:', error);
      return {
        status: 'offline',
        lastSeen: undefined,
        stats: []
      };
    }
  },
};

// Service de gestion des adresses IP autorisées
export const ipService = {
  // Récupérer toutes les adresses IP autorisées pour un client
  getAllowedIPs: async (clientId: string): Promise<AllowedIP[]> => {
    const response = await api.get<AllowedIP[]>(`/ip/${clientId}/allowed`);
    return response.data;
  },
  
  // Récupérer les informations de restriction IP pour un client
  getIPRestrictions: async (clientId: string): Promise<IPRestrictionInfo> => {
    const response = await api.get<IPRestrictionInfo>(`/ip/${clientId}/restrictions`);
    return response.data;
  },
  
  // Ajouter une adresse IP autorisée
  addAllowedIP: async (clientId: string, ipAddress: string): Promise<AllowedIP> => {
    const response = await api.post<AllowedIP>(`/ip/${clientId}/allowed`, { ipAddress });
    return response.data;
  },
  
  // Supprimer une adresse IP autorisée
  removeAllowedIP: async (clientId: string, ipId: string): Promise<void> => {
    await api.delete(`/ip/${clientId}/allowed/${ipId}`);
  },
  
  // Vérifier si une adresse IP est autorisée
  checkIPAllowed: async (clientId: string, ipAddress: string): Promise<{ allowed: boolean }> => {
    const response = await api.post<{ allowed: boolean }>(`/ip/check`, { clientId, ipAddress });
    return response.data;
  }
};

export default api;
